/**
 * Merges scenes from Redux state and API response
 *
 * This function intelligently combines scenes from two sources:
 * - If a scene is only in Redux, use it from Redux
 * - If a scene is only in the response, use it from the response
 * - If a scene is in both, use the one with the most recent timestamp
 *
 * @param {Array} reduxScenes - Array of scenes from Redux state
 * @param {Array} responseScenes - Array of scenes from API response
 * @returns {Array} Merged array of scenes with a 'source' property indicating origin
 *
 * @example
 * Expected result format:
 * [
 *     {
 *         "_id": "68fd7f0e02b6f9628a492b38",
 *         "chapterId": "68fd7be102b6f9628a492b1f",
 *         "id": "scene_1761443598407_i72ndkpcf",
 *         "name": "a",
 *         "description": null,
 *         "type": "paragraph",
 *         "position": 0,
 *         "parent": null,
 *         "children": [],
 *         "direction": "ltr",
 *         "format": 0,
 *         "indent": 0,
 *         "version": 1,
 *         "previousScene": null,
 *         "nextScene": null,
 *         "isDeleted": false,
 *         "deletedTimestamp": null,
 *         "createdAt": "2025-10-26T01:53:18.408Z",
 *         "updatedAt": "2025-10-27T02:47:38.340Z",
 *         "__v": 0,
 *         "data": {
 *             "root": {
 *                 "children": [
 *                     {
 *                         "children": [
 *                             {
 *                                 "detail": 0,
 *                                 "format": 0,
 *                                 "mode": "normal",
 *                                 "style": "",
 *                                 "text": "asdfgawgeagaegcaegaegaegeagawhghrhterghbgdyhs",
 *                                 "type": "text",
 *                                 "version": 1
 *                             }
 *                         ],
 *                         "direction": null,
 *                         "format": "",
 *                         "indent": 0,
 *                         "type": "paragraph",
 *                         "version": 1,
 *                         "textFormat": 0,
 *                         "textStyle": ""
 *                     }
 *                 ],
 *                 "direction": null,
 *                 "format": "",
 *                 "indent": 0,
 *                 "type": "root",
 *                 "version": 1,
 *                 "textFormat": 0
 *             }
 *         },
 *         "source": "api"
 *     },
 *     {
 *         "_id": "68fd7f1102b6f9628a492b42",
 *         "chapterId": "68fd7be102b6f9628a492b1f",
 *         "id": "scene_1761443601038_ddeum1w2a",
 *         "name": "b",
 *         "description": null,
 *         "type": "paragraph",
 *         "position": 0,
 *         "parent": null,
 *         "children": [],
 *         "direction": "ltr",
 *         "format": 0,
 *         "indent": 0,
 *         "version": 1,
 *         "previousScene": null,
 *         "nextScene": null,
 *         "isDeleted": false,
 *         "deletedTimestamp": null,
 *         "createdAt": "2025-10-26T01:53:21.039Z",
 *         "updatedAt": "2025-10-26T01:53:21.039Z",
 *         "__v": 0,
 *         "source": "api"
 *     }
 * ]
 */
/**
 * Redux editor slice stores Lexical JSON as { id, root, chapterId, ... }.
 * Scenes from the API use { id, data: { root } }. Normalize so UI always has scene.data.
 */
function normalizeSceneShape(scene) {
  if (!scene || typeof scene !== 'object') return scene;
  if (scene.data?.root) return scene;
  if (scene.root) {
    return {
      ...scene,
      data: { root: scene.root },
    };
  }
  return scene;
}

/**
 * @param {object} [options]
 * @param {boolean} [options.pruneReduxOnlyNotInApi] - When true and we have a fresh API list, drop scenes that exist only in Redux (stale client-only rows).
 */
const mergeScenes = (reduxScenes, responseScenes, options = {}) => {
  const { pruneReduxOnlyNotInApi = false } = options;

  // Create a map of scenes by ID
  const sceneMap = new Map();

  // Add scenes from Redux
  if (reduxScenes && Array.isArray(reduxScenes)) {
    reduxScenes.forEach((scene) => {
      if (scene?.id) {
        sceneMap.set(scene.id, { ...normalizeSceneShape(scene), source: 'redux' });
      }
    });
  }

  // Add or update scenes from API response
  if (responseScenes && Array.isArray(responseScenes)) {
    responseScenes.forEach((scene) => {
      if (scene?.id) {
        const existingScene = sceneMap.get(scene.id);

        if (existingScene) {
          // Scene exists in both, use the one with the most recent timestamp
          const existingTimestamp = new Date(
            existingScene.updatedAt || existingScene.lastUpdated || existingScene.timestamp || 0
          ).getTime();
          const responseTimestamp = new Date(scene.updatedAt || scene.timestamp || 0).getTime();

          if (responseTimestamp > existingTimestamp) {
            const apiNorm = normalizeSceneShape(scene);
            const reduxNorm = normalizeSceneShape(existingScene);
            // PUT + immediate GET often makes server updatedAt slightly newer than client lastUpdated.
            // If we replace the whole scene with API metadata, we can drop Lexical nodes the API
            // round-trip hasn't preserved yet (e.g. styled-text). Keep Redux `data` when it was
            // saved in the same window as this API row.
            const reduxSaveMs = new Date(existingScene.lastUpdated || 0).getTime();
            const LEXICAL_MERGE_WINDOW_MS = 120000;
            const keepReduxLexicalBody =
              Boolean(reduxNorm.data?.root) &&
              reduxSaveMs > 0 &&
              Math.abs(responseTimestamp - reduxSaveMs) < LEXICAL_MERGE_WINDOW_MS;

            sceneMap.set(scene.id, {
              ...apiNorm,
              ...(keepReduxLexicalBody ? { data: reduxNorm.data } : {}),
              source: keepReduxLexicalBody ? 'api-meta-redux-lexical' : 'api',
            });
          }
        } else {
          // Scene only exists in response
          sceneMap.set(scene.id, { ...normalizeSceneShape(scene), source: 'api' });
        }
      }
    });
  }

  let values = Array.from(sceneMap.values());
  if (pruneReduxOnlyNotInApi && Array.isArray(responseScenes)) {
    const allowed = new Set(responseScenes.map((s) => s?.id).filter(Boolean));
    if (allowed.size === 0) {
      values = [];
    } else {
      values = values.filter((s) => s?.id && allowed.has(s.id));
    }
  }

  return values;
};

export default mergeScenes;
